<script setup lang="ts">
import { DatepickerComponent } from '@metapic-apps/shared'

import type { ReturnAdFormType } from '@/lib/types/return-ad.types'
import { FlexComponent, InputComponent, RadioButton, RadioButtonGroupComponent, TitleComponent, TypographyComponent } from '@/ui-library'

defineProps<{ image?: string }>()

const hasStartsNow = defineModel<ReturnAdFormType['hasStartsNow']>('hasStartsNow')
const hasOpenEnd = defineModel<ReturnAdFormType['hasOpenEnd']>('hasOpenEnd')
const startsAt = defineModel<ReturnAdFormType['startsAt']>('startsAt')
const endsAt = defineModel<ReturnAdFormType['endsAt']>('endsAt')
const clickLimit = defineModel<ReturnAdFormType['clickLimit']>('clickLimit')
const viewLimit = defineModel<ReturnAdFormType['viewLimit']>('viewLimit')

const disableStartDate = (date: Date) => {
  return date.getTime() < Date.now() - 8.64e7 // number of milliseconds of a day
}

const disableEndDate = (date: Date) => {
  if (startsAt.value) {
    return date.getTime() < new Date(startsAt.value).getTime()
  }
  return disableStartDate(date)
}
</script>

<template>
  <FlexComponent :gap="24" direction="column">
    <TitleComponent title="Start" />
    <RadioButtonGroupComponent v-model="hasStartsNow" children-width="0%" name="hasStartNow">
      <RadioButton label="Start now" :value="true" />
      <RadioButton label="Start on date" :value="false" />
    </RadioButtonGroupComponent>
    <el-form-item v-if="!hasStartsNow" prop="startsAt">
      <DatepickerComponent
        v-model="startsAt"
        placeholder="Select start date"
        input-note="Based on UTC +02:00"
        name="startsAt"
        :disabled-date="disableStartDate"
      />
    </el-form-item>

    <TitleComponent title="End" />
    <RadioButtonGroupComponent v-model="hasOpenEnd" children-width="0%" name="hasOpenEnd">
      <RadioButton label="Open end" :value="true" />
      <RadioButton label="End on event" :value="false" />
    </RadioButtonGroupComponent>

    <template v-if="!hasOpenEnd">
      <el-form-item prop="validUntil">
        <DatepickerComponent
          v-model="endsAt"
          title="End on date"
          placeholder="Select end date"
          name="validUntil"
          input-note="Based on UTC +02:00"
          :disabled-date="disableEndDate"
        />
      </el-form-item>
      <el-form-item prop="clickLimit">
        <InputComponent v-model.integer="clickLimit" name="clickLimit">
          <template #header>
            <TypographyComponent variant="title-large">or click limit</TypographyComponent>
          </template>
        </InputComponent>
      </el-form-item>
      <el-form-item prop="viewLimit">
        <InputComponent v-model.integer="viewLimit" name="viewLimit">
          <template #header>
            <TypographyComponent variant="title-large">or view limit</TypographyComponent>
          </template>
        </InputComponent>
      </el-form-item>
    </template>
  </FlexComponent>
</template>
